import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
const EASE = [0.23, 1, 0.32, 1] as const;
export function ResearchSection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="research" className="bg-petrol text-sand py-20 lg:py-28" ref={ref} data-id="element-463" data-mp="3u1kvvrlei0xn">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-464" data-mp="2tyllg46f07t">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.55,
        ease: EASE
      }} className="max-w-[46rem]" data-id="element-465" data-mp="3kc1cmkts0529">
          <p className="esca-eyebrow text-accent" data-id="element-466" data-mp="gtrar70218do">{c.research.eyebrow}</p>
          <h2 className="esca-h2 mt-5 text-sand text-balance" data-id="element-467" data-mp="llj2hkuy5k5e">
            {c.research.title}
          </h2>
          <p className="esca-lead mt-6 text-petrol-100/70 max-w-[54ch]" data-id="element-468" data-mp="353s0ljctb2av">
            {c.research.lead}
          </p>
        </motion.div>

        {/* Narrowing strip */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.55,
        delay: 0.12,
        ease: EASE
      }} className="mt-14 lg:mt-16 border border-petrol-600" data-id="element-469" data-mp="3dusae4tdtnib">
          <div className="flex items-center px-4 py-2.5 border-b border-petrol-600" data-id="element-470" data-mp="3nrna2r1e5ty6">
            <span className="esca-eyebrow text-petrol-100/50" data-id="element-471" data-mp="3itbyaarjd9db">
              Search pipeline
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-id="element-472" data-mp="mecxp18ttf5">
            {c.research.funnel.map((f, i) => {
            const isLast = i === c.research.funnel.length - 1;
            return <div key={f.label} className="relative flex flex-col justify-center px-5 py-6 lg:py-8 border-b border-r border-petrol-600 last:border-r-0" data-id="element-473" data-mp="22hopa2gff4f9" data-mpi={i}>
                  <span className={`font-display text-2xl lg:text-[2rem] font-bold tabular-nums tracking-[-0.03em] leading-none ${isLast ? 'text-accent' : 'text-sand'}`} data-id="element-474" data-mp="6ch1ll1klys2">
                    {f.value}
                  </span>
                  <span className="mt-2 text-[0.75rem] tracking-wide uppercase text-petrol-100/45" data-id="element-475" data-mp="kd1l6jfb7ymm">
                    {f.label}
                  </span>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Stages */}
        <div className="mt-16 lg:mt-20 relative" data-id="element-476" data-mp="u4ebv7pdgir2">
          <div className="absolute left-[0.4375rem] top-2 bottom-2 w-px bg-petrol-600 lg:hidden" aria-hidden="true" data-id="element-477" data-mp="z1nzqix44mcs" />
          <ol className="grid lg:grid-cols-5 gap-10 lg:gap-6" data-id="element-478" data-mp="13yehdaokfq0b">
            {c.research.steps.map((step, i) => <motion.li key={step.title} initial={{
            opacity: 0,
            y: 14
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.2 + i * 0.07,
            ease: EASE
          }} className="relative pl-8 lg:pl-0 lg:pt-8" data-id="element-479" data-mp="2sk54kkogmioq" data-mpi={i}>
                <span className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-accent bg-petrol lg:top-0 lg:left-0" aria-hidden="true" data-id="element-480" data-mp="3c84exuf43ntm" />
                <div className="hidden lg:block absolute left-0 right-0 top-[0.4375rem] h-px bg-petrol-600 -z-0" aria-hidden="true" data-id="element-481" data-mp="32i1cl3m3oxim" />
                <span className="font-display text-sm font-semibold tabular-nums text-accent" data-id="element-482" data-mp="2xhdksfq1scnc">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-2 font-display text-lg lg:text-xl font-semibold text-sand tracking-[-0.02em]" data-id="element-483" data-mp="3q6aw1yvud96l">
                  {step.title}
                </h3>
                <p className="mt-2.5 text-[0.9375rem] text-petrol-100/60 leading-relaxed" data-id="element-484" data-mp="3erpnxhsfw7vb">
                  {step.text}
                </p>
              </motion.li>)}
          </ol>
        </div>
      </div>
    </section>;
}